/**
 * De ingelogde HubSpot-gebruiker.
 *
 * De app is per portaal geïnstalleerd, dus het JWT zegt alleen bij welk portaal
 * je hoort — niet wie je bent. De CRM-card kent de gebruiker wel (context.user)
 * en geeft die mee aan de iframe-URL. Hier bewaren we hem, zodat elke
 * API-aanroep kan vertellen wie de actie uitvoert. Zonder dit staat er in de
 * history van elk product dezelfde portaal-id in plaats van een naam.
 */

const STORAGE_KEY = 'hubspot_user';

export interface HubSpotUser {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const readStored = (): HubSpotUser | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as HubSpotUser) : null;
  } catch {
    return null;
  }
};

/** Leest de gebruiker uit de URL (gezet door de CRM-card) en onthoudt hem. */
export const captureHubSpotUserFromUrl = (): HubSpotUser | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);

  const user: HubSpotUser = {
    id: params.get('userId') || undefined,
    email: params.get('userEmail') || undefined,
    firstName: params.get('userFirstName') || undefined,
    lastName: params.get('userLastName') || undefined,
  };

  if (!user.id && !user.email) return readStored();

  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  return user;
};

export const getHubSpotUser = (): HubSpotUser | null => readStored();

export const getHubSpotUserName = (): string | null => {
  const user = readStored();
  if (!user) return null;
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.email || null;
};

/** Headers waarmee de backend de actor voor de history bepaalt. */
export const hubSpotUserHeaders = (): Record<string, string> => {
  const user = readStored();
  if (!user) return {};
  const headers: Record<string, string> = {};
  if (user.id) headers['x-hubspot-user-id'] = user.id;
  if (user.email) headers['x-hubspot-user-email'] = user.email;
  if (user.firstName) headers['x-hubspot-user-firstname'] = user.firstName;
  if (user.lastName) headers['x-hubspot-user-lastname'] = user.lastName;
  return headers;
};
